import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QUERY_KEYS } from '@/lib/constants';
import { useConfigureMFA, useEnableMFA, useVerifyMFA } from '../queries/useMFA';
import { extractSecretFromTOTPURI, MFA_FLOW_STAGES, MFAConfigSource, MFAFlowStage } from '../constants/mfa';
import { handleApiError } from '@/lib/utils/apiErrorHandler';

interface UseMFAConfigurationOptions {
    source: MFAConfigSource;
    onSuccess?: () => void;
    onClose?: () => void;
}

interface MFAConfigurationState {
    flowStage: MFAFlowStage;
    totpURI: string;
    secret: string;
    verifyRoute: string;
    configId: number | null;
    recoveryCode: string;
    authenticatorName: string;
    error: string | null;
    isLoading: boolean;
    showSetupKey: boolean;
    recoverySaveAction: 'copied' | 'downloaded' | null;
}

const initialState: MFAConfigurationState = {
    flowStage: MFA_FLOW_STAGES.METHOD_CHOICE,
    totpURI: '',
    secret: '',
    verifyRoute: '',
    configId: null,
    recoveryCode: '',
    authenticatorName: '',
    error: null,
    isLoading: false,
    showSetupKey: false,
    recoverySaveAction: null,
};

export function useMFAConfiguration({ source, onSuccess, onClose }: UseMFAConfigurationOptions) {
    const [state, setState] = useState<MFAConfigurationState>(initialState);
    const queryClient = useQueryClient();

    const { mutateAsync: configureMFA } = useConfigureMFA();
    const { mutateAsync: verifyMFA } = useVerifyMFA();
    const { mutateAsync: enableMFA } = useEnableMFA();

    // Update state helper
    const updateState = useCallback((updates: Partial<MFAConfigurationState>) => {
        setState(prev => ({ ...prev, ...updates }));
    }, []);

    // Reset all state
    const reset = useCallback(() => {
        setState(initialState);
    }, []);

    // Step 1: Fetch TOTP configuration (QR code data)
    const fetchConfiguration = useCallback(async () => {
        updateState({ isLoading: true, error: null });

        try {
            const response = await configureMFA();
            const secret = extractSecretFromTOTPURI(response.totp_uri);

            updateState({
                totpURI: response.totp_uri,
                secret,
                verifyRoute: response.route,
                flowStage: MFA_FLOW_STAGES.AUTHENTICATOR_APP,
                isLoading: false,
            });
        } catch (error) {
            handleApiError(error, 'Failed to configure MFA');
            updateState({ isLoading: false, error: 'Failed to configure MFA. Please try again.' });
        }
    }, [configureMFA, updateState]);

    // Step 2: Verify OTP code
    const verifyOTP = useCallback(
        async (code: string) => {
            if (!state.verifyRoute) {
                updateState({ error: 'Configuration not initialized' });
                return false;
            }

            updateState({ isLoading: true, error: null });

            try {
                const response = await verifyMFA({
                    verifyRoute: state.verifyRoute,
                    totp: code.trim(),
                    name: state.authenticatorName.trim() || 'Authenticator app',
                });

                updateState({
                    recoveryCode: response.recovery_text,
                    configId: response.config_id,
                    flowStage: MFA_FLOW_STAGES.SAVE_RECOVERY_CODES,
                    isLoading: false,
                });

                return true;
            } catch (error) {
                handleApiError(error, 'Invalid verification code');
                updateState({
                    isLoading: false,
                    error: 'Please enter a valid 6-digit code.',
                });
                return false;
            }
        },
        [state.verifyRoute, state.authenticatorName, verifyMFA, updateState]
    );

    // Step 3: Enable MFA with name
    const enableMFAWithName = useCallback(async () => {
        if (!state.configId) {
            updateState({ error: 'Configuration not found' });
            return false;
        }

        updateState({ isLoading: true, error: null });

        try {
            await enableMFA({
                configId: state.configId,
                name: state.authenticatorName.trim() || 'Authenticator app',
            });

            // Invalidate MFA list to refresh
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MFA_LIST] });

            updateState({
                flowStage: MFA_FLOW_STAGES.COMPLETE,
                isLoading: false,
            });

            return true;
        } catch (error) {
            handleApiError(error, 'Failed to enable MFA');
            updateState({
                isLoading: false,
                error: 'Failed to enable MFA. Please try again.',
            });
            return false;
        }
    }, [state.configId, state.authenticatorName, enableMFA, queryClient, updateState]);

    // Navigation helpers
    const goToMethodChoice = useCallback(() => {
        updateState({ flowStage: MFA_FLOW_STAGES.METHOD_CHOICE });
    }, [updateState]);

    const goToAuthenticatorSetup = useCallback(() => {
        fetchConfiguration();
    }, [fetchConfiguration]);

    const goToVerifyCode = useCallback(() => {
        updateState({ flowStage: MFA_FLOW_STAGES.VERIFY_CODE });
    }, [updateState]);

    const goToRecoveryCodes = useCallback(() => {
        updateState({ flowStage: MFA_FLOW_STAGES.SAVE_RECOVERY_CODES });
    }, [updateState]);

    const goToNameConfiguration = useCallback(() => {
        updateState({ flowStage: MFA_FLOW_STAGES.NAME_CONFIGURATION });
    }, [updateState]);

    const goToComplete = useCallback(() => {
        updateState({ flowStage: MFA_FLOW_STAGES.COMPLETE });
    }, [updateState]);

    // Toggle QR code / setup key view
    const toggleSetupKeyView = useCallback(() => {
        updateState({ showSetupKey: !state.showSetupKey });
    }, [state.showSetupKey, updateState]);

    // Set authenticator name
    const setAuthenticatorName = useCallback(
        (name: string) => {
            updateState({ authenticatorName: name });
        },
        [updateState]
    );

    // Set recovery save action
    const setRecoverySaveAction = useCallback(
        (action: 'copied' | 'downloaded') => {
            updateState({ recoverySaveAction: action });
        },
        [updateState]
    );

    // Clear error
    const clearError = useCallback(() => {
        updateState({ error: null });
    }, [updateState]);

    // Complete the flow
    const completeFlow = useCallback(() => {
        onSuccess?.();
        onClose?.();
        reset();
    }, [onSuccess, onClose, reset]);

    return {
        // State
        ...state,
        source,

        // Computed
        canProceedFromRecovery: state.recoverySaveAction !== null,
        canFinishSetup: state.authenticatorName.trim().length > 0,

        // Actions
        fetchConfiguration,
        verifyOTP,
        enableMFAWithName,
        reset,
        clearError,
        completeFlow,

        // Navigation
        goToMethodChoice,
        goToAuthenticatorSetup,
        goToVerifyCode,
        goToRecoveryCodes,
        goToNameConfiguration,
        goToComplete,

        // UI helpers
        toggleSetupKeyView,
        setAuthenticatorName,
        setRecoverySaveAction,
    };
}

export type MFAConfigurationHook = ReturnType<typeof useMFAConfiguration>;
